// Shared "Invite your Technical Lead" modal.
//
// EXTRACTED verbatim from OnboardingPanel.tsx (ONB.B1-FIX.A1) so the dashboard
// panel AND the focused /setup hub (SetupWizardPage) drive the SAME TL-invite
// flow — single source of truth, no drift. Both hosts render this modal for the
// invite_technical_lead task; previously the hub's CTA mis-routed to
// /settings/organisation (the generic team-invite modal, which never sets
// technical_lead_user_id). #1247 access-regression follow-on.
//
// Submit POSTs to /setup-projects/:id/invite-technical-lead which atomically
// writes setup_invitations AND advances setup_tasks.invite_technical_lead →
// in_progress in one transaction. On success the host closes the modal and
// refetches its own task state (onSuccess); the open/close lifecycle is host-
// owned so each surface refreshes via the poll/refetch it already uses.
//
// a11y (carried verbatim): role="dialog" + aria-modal="true" on the content
// container; aria-labelledby points to the h3 title id.

import { useEffect, useRef, useState } from 'react';
import { apiFetch } from '../lib/api';

// roleHint enum values mirror apps/api/src/routes/setup-invitations.routes.ts
// createInviteBody. Order = dropdown display order; first value = default.
export type RoleHint = 'ENGINEERING_MANAGER' | 'CTO' | 'DEVOPS_LEAD' | 'IT_ADMIN' | 'OTHER';
const ROLE_OPTIONS: { value: RoleHint; label: string }[] = [
  { value: 'ENGINEERING_MANAGER', label: 'Engineering Manager' },
  { value: 'CTO',                 label: 'CTO' },
  { value: 'DEVOPS_LEAD',         label: 'DevOps Lead' },
  { value: 'IT_ADMIN',            label: 'IT Admin' },
  { value: 'OTHER',               label: 'Other' },
];

interface Props {
  /** The active setup project the invite is scoped to. */
  setupProjectId: string;
  /**
   * Fired on a successful invite (HTTP 2xx). The host is responsible for
   * closing the modal AND refreshing its task state — mirrors the panel's
   * original "close + immediate refetch" behaviour.
   */
  onSuccess: () => void;
  /** Fired on Cancel / backdrop dismiss (only while no request is in flight). */
  onClose: () => void;
}

export default function InviteTechnicalLeadModal({ setupProjectId, onSuccess, onClose }: Props) {
  const [email, setEmail]       = useState('');
  const [name, setName]         = useState('');
  const [role, setRole]         = useState<RoleHint>('ENGINEERING_MANAGER');
  const [inviting, setInviting] = useState(false);
  const [error, setError]       = useState<string | null>(null);

  // Guard setState after unmount — the host typically unmounts this modal in
  // its onSuccess handler, so the trailing `.finally` can resolve post-unmount.
  const unmounted = useRef(false);
  useEffect(() => {
    unmounted.current = false;
    return () => { unmounted.current = true; };
  }, []);

  // On 2xx we hand control back to the host (close + refetch). On 4xx/5xx the
  // server message is surfaced inline and the modal stays open.
  const handleSubmit = (): Promise<void> => {
    setInviting(true);
    setError(null);
    return apiFetch(`/setup-projects/${setupProjectId}/invite-technical-lead`, {
      method: 'POST',
      body: JSON.stringify({
        invitedEmail: email,
        invitedName:  name,
        roleHint:     role,
      }),
    })
      .then(() => {
        if (unmounted.current) return;
        onSuccess();
      })
      .catch((err: unknown) => {
        if (unmounted.current) return;
        const msg = err instanceof Error
          ? err.message
          : 'Could not send invite. Please try again.';
        setError(msg);
      })
      .finally(() => {
        if (!unmounted.current) setInviting(false);
      });
  };

  const submitDisabled = inviting || !email.trim() || !name.trim();

  return (
    <div
      data-testid="invite-tech-lead-modal"
      style={{
        position: 'fixed', inset: 0, zIndex: 100,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'rgba(0,0,0,0.4)',
      }}
      onClick={() => { if (!inviting) onClose(); }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="invite-tech-lead-title"
        style={{
          background: 'var(--surface)', borderRadius: 'var(--radius-lg)',
          padding: 24, width: 420, maxHeight: '85vh', overflowY: 'auto',
          boxShadow: 'var(--shadow-lg)',
        }}
        onClick={e => e.stopPropagation()}
      >
        <h3 id="invite-tech-lead-title" style={{ fontFamily: 'var(--font-display)', fontSize: 16, fontWeight: 600, color: 'var(--text)', marginBottom: 16 }}>
          Invite your Technical Lead
        </h3>

        <label htmlFor="invite-tech-lead-name" style={{ display: 'block', fontSize: 12, fontWeight: 500, color: 'var(--text-secondary, #6b7280)', marginBottom: 4 }}>
          Their name
        </label>
        <input
          id="invite-tech-lead-name"
          data-testid="invite-tech-lead-name"
          type="text"
          value={name}
          onChange={e => setName(e.target.value)}
          placeholder="Jane Engineer"
          maxLength={120}
          style={{
            width: '100%', padding: '8px 10px', marginBottom: 12,
            border: '1px solid var(--border)', borderRadius: 6,
            fontFamily: 'var(--font-body)', fontSize: 14, boxSizing: 'border-box',
          }}
        />

        <label htmlFor="invite-tech-lead-email" style={{ display: 'block', fontSize: 12, fontWeight: 500, color: 'var(--text-secondary, #6b7280)', marginBottom: 4 }}>
          Their email
        </label>
        <input
          id="invite-tech-lead-email"
          data-testid="invite-tech-lead-email"
          type="email"
          value={email}
          onChange={e => setEmail(e.target.value)}
          placeholder="jane@company.com"
          style={{
            width: '100%', padding: '8px 10px', marginBottom: 12,
            border: '1px solid var(--border)', borderRadius: 6,
            fontFamily: 'var(--font-body)', fontSize: 14, boxSizing: 'border-box',
          }}
        />

        <label htmlFor="invite-tech-lead-role" style={{ display: 'block', fontSize: 12, fontWeight: 500, color: 'var(--text-secondary, #6b7280)', marginBottom: 4 }}>
          Their role
        </label>
        <select
          id="invite-tech-lead-role"
          data-testid="invite-tech-lead-role"
          value={role}
          onChange={e => setRole(e.target.value as RoleHint)}
          style={{
            width: '100%', padding: '8px 10px', marginBottom: 16,
            border: '1px solid var(--border)', borderRadius: 6,
            fontFamily: 'var(--font-body)', fontSize: 14, boxSizing: 'border-box',
            background: 'var(--surface)',
          }}
        >
          {ROLE_OPTIONS.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>

        {error && (
          <p
            data-testid="invite-tech-lead-error"
            style={{ fontSize: 13, color: '#ef4444', marginBottom: 12, marginTop: 0 }}
          >
            {error}
          </p>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button
            data-testid="invite-tech-lead-cancel"
            onClick={() => onClose()}
            disabled={inviting}
            style={{
              padding: '8px 14px', borderRadius: 6,
              background: 'transparent', color: 'var(--text-secondary, #6b7280)',
              border: '1px solid var(--border)', cursor: inviting ? 'not-allowed' : 'pointer',
              fontSize: 14, opacity: inviting ? 0.6 : 1,
            }}
          >
            Cancel
          </button>
          <button
            data-testid="invite-tech-lead-submit"
            onClick={() => void handleSubmit()}
            disabled={submitDisabled}
            style={{
              padding: '8px 14px', borderRadius: 6,
              background: '#7c3aed', color: '#fff',
              border: 'none', fontWeight: 500,
              cursor: submitDisabled ? 'not-allowed' : 'pointer',
              fontSize: 14,
              opacity: submitDisabled ? 0.6 : 1,
            }}
          >
            {inviting ? 'Sending…' : 'Send invite'}
          </button>
        </div>
      </div>
    </div>
  );
}
